import { useCallback, useEffect, useRef, useState } from "react";
import { useSetRecoilState, useRecoilValue } from "recoil";

import {
  ActivePlaylistClipState,
  PlaylistClipsState,
  PlaylistIdState,
  VisiblePlaylistClipsState,
} from "MVVM/State/embeddablePlaylist";
import { filterActiveClips } from "../../ViewModel";
import { formatPlaylistClip } from "../../Utilities";
import { PlaylistClip } from "../../@types";
import {
  IUggContext,
  TUggState,
  canCreate,
  hasCreateInputs,
  isAssertionExpired,
  mapCreateResponse,
  readUggContext,
} from "./params";

// ponytail: the create response carries no clip id, so we poll the lookup instead.
// /league/clip/status is the upgrade path, but it rejects the public key today.
// A League render regularly runs past two minutes, so the budget must outlast it.
const POLL_INTERVAL_MS = 6000;
const POLL_ATTEMPTS = 50;
const IN_FLIGHT_STATUSES = ["submitted", "queued"];
const PLAYLIST_RETRY_MS = 1500;
const LOOKUP_RETRIES = 2;
const LOOKUP_RETRY_MS = 800;

interface ILookup {
  name: string | null;
  inFlight: boolean;
  hasPlayable: boolean;
}

export interface IUggHighlightsTab {
  state: TUggState | "loading";
  context: IUggContext;
  clips: PlaylistClip[];
  submitting: boolean;
  createHighlights: () => void;
}

export const UGGHighlightsTabViewModel = ({
  platform,
}: {
  platform: string;
}): IUggHighlightsTab => {
  const setPlaylistClips = useSetRecoilState(PlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const setVisiblePlaylistClips = useSetRecoilState(VisiblePlaylistClipsState);
  const playlistId = useRecoilValue(PlaylistIdState);

  const [context] = useState<IUggContext>(() =>
    readUggContext(window.location.search),
  );
  const [clips, setClips] = useState<PlaylistClip[]>([]);
  const [fetching, setFetching] = useState(true);
  const [createState, setCreateState] = useState<TUggState | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const inFlightRef = useRef(false);
  const runId = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [hasPlayable, setHasPlayable] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  // matchId must be the bare id: the server builds region_match as `${REGION}_${matchId}`.
  const lookupMatch = useCallback(async (): Promise<ILookup> => {
    if (!hasCreateInputs(context))
      return { name: null, inFlight: false, hasPlayable: false };

    const query =
      `platform=${encodeURIComponent(platform)}` +
      `&matchId=${encodeURIComponent(context.matchId as string)}` +
      `&region=${encodeURIComponent(context.region as string)}` +
      `&riotID=${encodeURIComponent(context.riotID as string)}`;

    const read = async (url: string) => {
      const res = await fetch(url, {
        headers: { "X-API-Key": process.env.REACT_APP_UGG_PUBLIC_KEY || "" },
      });
      // Only an unreachable API counts as a failure; an unparseable body reads as empty.
      const body = await res.json().catch(() => null);
      return body?.data;
    };

    // Not the playlist endpoint: it drops in-progress clips, so a mid-render reload
    // would re-offer create. Only this one honours inProgress and always returns an array.
    const found = await read(
      `${process.env.REACT_APP_PARTNER_URL}/league/clips?${query}&inProgress=1`,
    );
    const clips: PlaylistClip[] = found?.clips || [];
    const inFlight = clips.some((clip) =>
      IN_FLIGHT_STATUSES.includes(clip?.status?.toLowerCase()),
    );

    const hasPlayable = clips.some(
      (clip) => !IN_FLIGHT_STATUSES.includes(clip?.status?.toLowerCase()),
    );
    if (!hasPlayable) return { name: null, inFlight, hasPlayable };

    const playlist = `${process.env.REACT_APP_PARTNER_URL}/league/clips/matchPlaylist?${query}`;
    const data = await read(playlist);
    const name = data?.playlistUrl
      ? new URL(data.playlistUrl).searchParams.get("playlist")
      : null;

    return { name, inFlight, hasPlayable };
  }, [context, platform]);

  const loadClips = useCallback(
    async (name: string) => {
      const encodedPlatform = encodeURIComponent(platform);
      // Env-driven on purpose: OPGGEmbeddablePlaylistViewModel hardcodes prt.allstar.gg,
      // so staging hits prod there.
      const url = `${process.env.REACT_APP_PARTNER_URL}/playlist?name=${name}&platform=${encodedPlatform}&fmt=iframe`;

      const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      const active = filterActiveClips(data?.data?.clips || []);

      if (cancelled.current || !active.length) return active;

      setActivePlaylistClip(formatPlaylistClip(active[0]));
      setPlaylistClips(active);
      setVisiblePlaylistClips(active.slice(0, 5));
      setClips(active);

      return active;
    },
    [
      platform,
      setActivePlaylistClip,
      setPlaylistClips,
      setVisiblePlaylistClips,
    ],
  );

  const refresh = useCallback(async () => {
    if (playlistId) return loadClips(playlistId);

    const { name, inFlight, hasPlayable } = await lookupMatch();
    inFlightRef.current = inFlight;
    if (!cancelled.current) {
      setInFlight(inFlight);
      setHasPlayable(hasPlayable);
    }
    if (!name) return [];

    const found = await loadClips(name);
    // The playlist is rebuilt per request and reads back empty for a beat after a clip
    // lands, so re-read rather than reporting a match we know has one as empty.
    if (found.length || !hasPlayable) return found;

    await new Promise((resolve) => setTimeout(resolve, PLAYLIST_RETRY_MS));
    return loadClips(name);
  }, [playlistId, lookupMatch, loadClips]);

  useEffect(() => {
    // Runs overlap once playlistId settles, and only the newest may clear `fetching`:
    // an older one finishing first would flash the signed-out screen over a real clip.
    const id = (runId.current += 1);

    setFetching(true);
    const run = async () => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          return await refresh();
        } catch (error) {
          if (attempt >= LOOKUP_RETRIES) throw error;
          await new Promise((resolve) =>
            setTimeout(resolve, LOOKUP_RETRY_MS * (attempt + 1)),
          );
        }
      }
    };

    run()
      .then(() => !cancelled.current && setLookupFailed(false))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn("[UGG] clip lookup failed:", error);
        if (!cancelled.current) setLookupFailed(true);
      })
      .finally(() => {
        if (!cancelled.current && id === runId.current) setFetching(false);
      });
  }, [refresh]);

  useEffect(() => {
    if (createState !== "generating" && !inFlight) return;

    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      const found = await refresh().catch(() => []);
      if (found.length) return clearInterval(timer);

      if (attempts >= POLL_ATTEMPTS) {
        clearInterval(timer);
        // Never call a clip we can still see "no highlights"; it is only slow.
        if (!cancelled.current && !inFlightRef.current) setCreateState("none");
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [createState, inFlight, refresh]);

  const createHighlights = useCallback(async () => {
    if (!canCreate(context) || !hasCreateInputs(context)) return;

    setSubmitting(true);

    const url =
      `${process.env.REACT_APP_PARTNER_URL}/league/clip/bp` +
      `?platform=${encodeURIComponent(platform)}` +
      `&assertion=${encodeURIComponent(context.assertion as string)}`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": process.env.REACT_APP_UGG_PUBLIC_KEY || "",
        },
        // Signed requests take a strict allowlist: matchId, region, puuid, riotID.
        // Anything else (metadata, trackId, webhookUrl) is rejected outright.
        body: JSON.stringify({
          riotID: context.riotID,
          matchId: context.matchId,
          region: context.region,
        }),
      });

      const body = await res.json().catch(() => ({}));

      if (res.status === 400 && !body?.code) {
        // eslint-disable-next-line no-console
        console.warn("[UGG] clip create rejected:", body?.message);
      }

      // 201 renders, 200 is a dry run that silently never produces a clip.
      if (res.status === 200) {
        // eslint-disable-next-line no-console
        console.warn(
          "[UGG] clip create returned 200: partner signedAuth.dryRun is on, no clip will be produced.",
        );
      }

      if (!cancelled.current) {
        setCreateState(
          mapCreateResponse(res.status, body?.code, body?.message),
        );
      }
    } catch {
      if (!cancelled.current) setCreateState("unsupported");
    } finally {
      if (!cancelled.current) setSubmitting(false);
    }
  }, [context, platform]);

  const resolveState = (): TUggState | "loading" => {
    if (context.forcedState) return context.forcedState;
    if (fetching) return "loading";
    if (clips.length) return "ready";
    if (createState) return createState;
    if (inFlight) return "generating";
    if (hasPlayable) return "generating";
    // Everything below says "nothing to show", so only reach it once we know that.
    // A broken integration or a failed lookup is never a signed-out viewer.
    if (!hasCreateInputs(context)) return "unsupported";
    if (lookupFailed) return canCreate(context) ? "create" : "unsupported";
    if (!canCreate(context)) return "signedout";
    if (isAssertionExpired(context)) return "expired";
    return "create";
  };

  const visibleClips =
    context.forcedState === "ready" ? clips.slice(0, 1) : clips;

  return {
    state: resolveState(),
    context,
    clips: visibleClips,
    submitting,
    createHighlights,
  };
};
